import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useUserService } from "../services/userService";
import { useTenant } from "../contexts/tenant-context";
import { TenantSelector } from "../components/common/tenant-selector";

const AuthLoadingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserSession } = useUserService();
  const { setUserSession, setSelectedTenantId, selectedTenantId } = useTenant();
  const [showTenantSelector, setShowTenantSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const bootstrapCalled = useRef(false);

  const fromPath =
    (location.state as { from?: string } | null)?.from ?? "/dashboard";

  useEffect(() => {
    // Prevent double-execution (React StrictMode or dependency changes)
    if (bootstrapCalled.current) return;
    bootstrapCalled.current = true;

    const bootstrap = async () => {
      try {
        const session = await getUserSession();
        setUserSession(session);

        // Check if user is disabled
        if (!session.user.isEnabled) {
          navigate("/no-access", { state: { reason: 'no-permissions' } });
          return;
        }

        // If user already has a tenant selected (from localStorage), validate it against current session
        if (selectedTenantId) {
          const isValidTenant =
            selectedTenantId === '__GLOBAL_ADMIN__'
              ? session.isGlobalAdmin
              : session.tenants.some(t => t.tenantId === selectedTenantId);

          if (isValidTenant) {
            setIsLoading(false);
            navigate(fromPath, { replace: true });
            return;
          }

          // Cached tenant is no longer valid for this user, clear it
          setSelectedTenantId(null);
        }

        // Handle Global Admin with no tenants - auto-select Global Admin option
        if (session.isGlobalAdmin && session.tenants.length === 0) {
          setSelectedTenantId('__GLOBAL_ADMIN__');
          setIsLoading(false);
          navigate(fromPath, { replace: true });
          return;
        }

        // Handle Global Admin with tenants - show selector (includes Global Admin + tenant options)
        if (session.isGlobalAdmin && session.tenants.length > 0) {
          setIsLoading(false);
          setShowTenantSelector(true);
          return;
        }

        // Handle no tenants (brand new user or no access)
        if (session.tenants.length === 0) {
          navigate("/no-access", { state: { reason: 'no-permissions' } });
          return;
        }

        // Handle single tenant - auto select
        if (session.tenants.length === 1) {
          setSelectedTenantId(session.tenants[0].tenantId);
          setIsLoading(false);
          navigate(fromPath, { replace: true });
          return;
        }

        // Handle multiple tenants - show selector
        setIsLoading(false);
        setShowTenantSelector(true);
      } catch (error) {
        console.error("Auth loading error:", error);
        navigate("/no-access", { state: { reason: 'no-permissions' } });
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTenantSelect = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setShowTenantSelector(false);
    navigate(fromPath, { replace: true });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {showTenantSelector && (
        <TenantSelector
          open={showTenantSelector}
          onSelect={handleTenantSelect}
          canClose={false}
        />
      )}
    </>
  );
};

export default AuthLoadingPage;
